// ===== ReportsSidebar.js =====
import React, { useState, useEffect, useCallback } from 'react';

const ReportsSidebar = ({
  isOpen,
  onClose,
  reports,
  isLoading,
  onLoadReports,
  onViewReport,
  onDeleteReport,
}) => {
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [deletingReports, setDeletingReports] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (isOpen && !loadedOnce && onLoadReports) {
      onLoadReports();
      setLoadedOnce(true);
    }
  }, [isOpen, loadedOnce, onLoadReports]);

  useEffect(() => {
    if (!isOpen) {
      setLoadedOnce(false);
      setConfirmDelete(null);
    }
  }, [isOpen]);

  // Clear confirm state when the report disappears from the list (i.e. deleted)
  useEffect(() => {
    if (confirmDelete && reports && !reports.find(r => r.sessionId === confirmDelete)) {
      setConfirmDelete(null);
      setDeletingReports(prev => {
        const next = new Set(prev);
        next.delete(confirmDelete);
        return next;
      });
    }
  }, [reports, confirmDelete]);

  const handleRefresh = useCallback(() => {
    if (onLoadReports) onLoadReports();
  }, [onLoadReports]);

  const handleReportClick = useCallback(
    (sessionId) => {
      if (confirmDelete) return; // Don't navigate while confirming delete
      if (onViewReport) onViewReport(sessionId);
      onClose();
    },
    [onViewReport, onClose, confirmDelete]
  );

  const handleDeleteClick = useCallback((e, sessionId) => {
    e.stopPropagation();
    setConfirmDelete(sessionId);
  }, []);

  const handleConfirmDelete = useCallback((e, sessionId) => {
    e.stopPropagation();
    console.log('🔴 ReportsSidebar: handleConfirmDelete called for:', sessionId);
    console.log('🔴 ReportsSidebar: onDeleteReport is:', typeof onDeleteReport);
    if (onDeleteReport) {
      setDeletingReports(prev => new Set(prev).add(sessionId));
      onDeleteReport(sessionId);
    } else {
      console.error('🔴 ReportsSidebar: onDeleteReport is undefined!');
    }
    // Auto-clear after timeout in case WS response doesn't come
    setTimeout(() => {
      setDeletingReports(prev => {
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
      setConfirmDelete(null);
    }, 10000);
  }, [onDeleteReport]);

  const handleCancelDelete = useCallback((e) => {
    e.stopPropagation();
    setConfirmDelete(null);
  }, []);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/25 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 z-50 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: '360px', maxWidth: '85vw' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Reports
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Refresh */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/30 transition-colors"
          >
            {isLoading ? (
              <>
                <svg
                  className="w-4 h-4 mr-2 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading…
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh Reports
              </>
            )}
          </button>
        </div>

        {/* Report list */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (!reports || reports.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-3" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Loading saved reports…
              </span>
            </div>
          ) : !reports || reports.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No saved reports found
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Reports are saved automatically during research
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {reports.map((report, i) => {
                const isDeleting = deletingReports.has(report.sessionId);
                const isConfirming = confirmDelete === report.sessionId;

                return (
                  <div
                    key={report.sessionId || i}
                    className={`relative w-full text-left p-4 rounded-lg border transition-all duration-200 group ${
                      isDeleting
                        ? 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 opacity-50'
                        : isConfirming
                        ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-green-50 dark:hover:bg-green-900/10 hover:border-green-300 dark:hover:border-green-700 cursor-pointer'
                    }`}
                    onClick={() => !isDeleting && !isConfirming && handleReportClick(report.sessionId)}
                  >
                    {/* Confirm delete overlay */}
                    {isConfirming && !isDeleting && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/90 dark:bg-gray-800/90 rounded-lg z-10">
                        <div className="text-center px-4">
                          <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                            Delete this report?
                          </p>
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={(e) => handleConfirmDelete(e, report.sessionId)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                            >
                              Delete
                            </button>
                            <button
                              onClick={handleCancelDelete}
                              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Deleting spinner overlay */}
                    {isDeleting && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 rounded-lg z-10">
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">Deleting…</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-1.5">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <svg
                          className="w-4 h-4 text-green-500 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors">
                          {report.reportTitle || 'Untitled Report'}
                        </span>
                      </div>

                      <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                        {/* Delete button */}
                        {!isConfirming && !isDeleting && (
                          <button
                            onClick={(e) => handleDeleteClick(e, report.sessionId)}
                            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-all"
                            title="Delete report"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                        {/* Chevron */}
                        <svg
                          className="w-4 h-4 text-gray-400 group-hover:text-green-500 transition-colors"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pl-6">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {report.sessionId?.slice(0, 16)}…
                      </span>
                      <div className="flex items-center space-x-3">
                        {report.wordCount > 0 && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {report.wordCount.toLocaleString()} words
                          </span>
                        )}
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {report.lastModified
                            ? new Date(report.lastModified).toLocaleDateString()
                            : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReportsSidebar;